/**
 * GoodShowHQ public landing page — conversion-focused redesign.
 *
 * Recreated from the design handoff "GoodShowHQ Landing Page Redesign"
 * (high-fidelity: colors/type/spacing/copy are final intent) onto the
 * site's existing stack: MUI 7 + `sx`, wrapped in the marketing
 * `goodShowHQTheme`. Brand tokens are authored in OKLCH (the handoff's
 * ramps) and used directly as CSS color strings in `sx` for full
 * fidelity — the same local-`C`-object pattern as DownloadsPage.
 *
 * Section order (single page, top to bottom):
 *   1. Nav        2. Hero          3. The difference (#difference)
 *   4. How it works (#how)         5. Testimonials
 *   6. Get the app (#download)     7. FAQ (#faq)
 *   8. Waitlist close (#join)      9. Footer
 *
 * HashRouter note: in-page anchors scroll imperatively (`scrollToId`) —
 * `href="#how"` would collide with the hash router. Cross-page links use
 * `#/…`. Mounted at `/` in index.tsx.
 */
import React, {useRef, useState} from 'react';
import {Box, CssBaseline, ThemeProvider, Typography} from '@mui/material';
import {goodShowHQTheme} from '../../theme/planapusTheme';
import {DESKTOP_DOWNLOADS} from '../../lib/releases';
import logoMark from '../../assets/brand/logo-mark.svg';

/* ---- design tokens (OKLCH ramps from the handoff) -------------------- */
const C = {
  bg: '#ffffff',
  ink: 'oklch(0.22 0.03 275)',
  accent: 'oklch(0.50 0.25 268)',
  accent100: 'oklch(0.95 0.03 268)',
  accent200: 'oklch(0.90 0.06 268)',
  accent600: 'oklch(0.45 0.24 268)',
  accent700: 'oklch(0.40 0.21 268)',
  // periwinkle — same hue, low chroma (the only secondary voice)
  p100: 'oklch(0.955 0.022 268)',
  p200: 'oklch(0.91 0.045 268)',
  p800: 'oklch(0.37 0.11 268)',
  n100: 'oklch(0.965 0.006 280)',
  n200: 'oklch(0.93 0.009 280)',
  n700: 'oklch(0.44 0.03 280)',
  navHover: 'oklch(0.32 0.03 275)',
};
/** ink at N% opacity — secondary (78) / muted (60–70) text. */
const ink = (pct: number) => `color-mix(in srgb, ${C.ink} ${pct}%, transparent)`;

const FONT_HEAD = '"Caprasimo", Georgia, "Times New Roman", serif';
const FONT_BODY =
  '"Figtree", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const MAXW = 1160;
const PADX = 'clamp(20px, 5vw, 72px)';

/* ---- icons (Lucide geometry, stroke 2.75) ---------------------------- */
function Stroke({d, size = 15}: {d: string; size?: number}): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}
const PlayIcon = <Stroke d="m6 4 14 8-14 8V4z" />;
const ChevIcon = <Stroke d="m6 9 6 6 6-6" size={16} />;
const LaptopIcon = (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x={3} y={4} width={18} height={12} rx={2} />
    <path d="M2 20h20" />
  </svg>
);
const AppleIcon = (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

/* ---- data ------------------------------------------------------------ */
const BEFORE_ROWS = [
  {title: 'I SURVIVED 100 DAYS OF ___!!!', tag: 'Drama'},
  {title: 'HE BROKE UP WITH HER ON TIKTOK', tag: 'Drama'},
  {title: 'UNBOXING MY $5,000 HAUL', tag: 'Ads'},
  {title: 'WHY EVERYONE IS MAD AT ___', tag: 'Ragebait'},
];
const AFTER_ROWS = [
  {title: 'How volcanoes actually work', tag: 'Science'},
  {title: 'Learn chess in 10 minutes', tag: 'Strategy'},
  {title: 'Why do we dream?', tag: 'Biology'},
  {title: 'The history of the pencil', tag: 'History'},
];
const STEPS = [
  {
    n: '1',
    bg: C.accent100,
    fg: C.accent700,
    title: 'Connect',
    body: 'A 60-second setup, from your own phone. Nothing installs on your kid’s device, and their YouTube looks exactly the same.',
  },
  {
    n: '2',
    bg: C.accent200,
    fg: C.accent700,
    title: 'Tell us what matters',
    body: 'The subjects your kid loves, the pace you prefer, the channels you already trust. Two minutes, changeable any time.',
  },
  {
    n: '3',
    bg: C.p200,
    fg: C.p800,
    title: 'Watch the feed turn',
    body: 'Over a week or two the junk quietly fades and the good stuff takes its place. Your kid just thinks YouTube got better.',
  },
];
const TESTIMONIALS = [
  {q: 'The unboxing videos just… stopped showing up. She didn’t notice. I noticed.', by: 'Priya S. · pilot parent'},
  {q: 'I didn’t have to take away her device or uninstall YouTube. It’s the same YouTube as before — just better.', by: 'Marcus T. · pilot parent'},
  {q: 'GoodShow tells me what he’s watching — the good shows and the bad ones too — so we can actually talk about it.', by: 'Elena R. · pilot parent'},
];
const FAQS = [
  {q: 'Does GoodShowHQ replace YouTube?', a: 'No. YouTube is full of great things — the problem is finding them under everything else. GoodShowHQ works inside the YouTube your kid already uses, nudging the feed toward the good stuff and letting the junk fade.'},
  {q: 'How is this different from YouTube Kids?', a: 'YouTube Kids is a walled garden most kids outgrow by seven. GoodShowHQ works on regular YouTube — the one your kid actually wants to use — so there’s nothing to outgrow and nothing to rebel against.'},
  {q: 'Is my child’s data safe?', a: 'Yes. We review video content, not your child. We never sell data, never build advertising profiles, and we’re COPPA-compliant. You can delete everything with one tap, any time.'},
  {q: 'What does “COPPA-compliant” actually mean?', a: 'COPPA is the U.S. Children’s Online Privacy Protection Act — the federal law protecting kids under 13 online. For us it means: we never collect personal information from your child, everything runs through your parent account with your verifiable consent, we never sell or share data or build advertising profiles, and you can see and delete everything we hold, any time, with one tap.'},
  {q: 'Will my kid know it’s there?', a: 'That’s up to you. Nothing appears on their device and their YouTube looks the same — just gradually better. Many parents tell their kids; plenty don’t. We think honesty works best, but it’s your call.'},
];

const MAC_URL = DESKTOP_DOWNLOADS.find(d => d.id === 'mac-apple-silicon')?.url ?? '#/downloads';
const WIN_URL = DESKTOP_DOWNLOADS.find(d => d.id === 'windows')?.url ?? '#/downloads';

function scrollToId(id: string): void {
  document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
}

/* ---- shared bits ----------------------------------------------------- */
const btnBase = {
  fontFamily: FONT_BODY,
  fontWeight: 700,
  fontSize: 15.5,
  lineHeight: 1.1,
  borderRadius: 999,
  px: '22px',
  minHeight: 44,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  textDecoration: 'none',
  cursor: 'pointer',
  border: '1.5px solid transparent',
  transition: 'background .15s, color .15s, border-color .15s',
  '&:focus-visible': {outline: `2px solid ${C.accent}`, outlineOffset: 2},
} as const;
const btnPrimary = {...btnBase, bgcolor: C.accent, color: '#fff', '&:hover': {bgcolor: C.accent600}};
const btnSecondary = {
  ...btnBase,
  bgcolor: 'transparent',
  color: C.ink,
  borderColor: ink(18),
  '&:hover': {borderColor: ink(34), bgcolor: ink(4)},
};
const navPrimary = {...btnBase, bgcolor: C.ink, color: '#fff', '&:hover': {bgcolor: C.navHover}};

function Kicker({children, sx}: {children: React.ReactNode; sx?: object}): React.JSX.Element {
  return (
    <Typography
      component="span"
      sx={{
        display: 'block',
        fontSize: 13,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
        color: C.accent700,
        mb: '16px',
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function H2({children, sx}: {children: React.ReactNode; sx?: object}): React.JSX.Element {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: FONT_HEAD,
        fontWeight: 400,
        fontSize: 'clamp(28px, 3vw, 38px)',
        lineHeight: 1.2,
        m: 0,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function FeedRow({
  title,
  tag,
  variant,
}: {
  title: string;
  tag: string;
  variant: 'before' | 'after';
}): React.JSX.Element {
  const after = variant === 'after';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        bgcolor: C.bg,
        borderRadius: 999,
        p: '10px 18px 10px 12px',
      }}
    >
      <Box
        sx={{
          flex: 'none',
          width: 30,
          height: 30,
          borderRadius: '50%',
          bgcolor: after ? C.p200 : C.n200,
          color: after ? C.p800 : C.n700,
          display: 'grid',
          placeContent: 'center',
        }}
      >
        {PlayIcon}
      </Box>
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 14.5,
          fontWeight: 600,
          lineHeight: 1.35,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </Typography>
      <Box
        component="span"
        sx={{
          flex: 'none',
          fontSize: 12.5,
          fontWeight: 600,
          borderRadius: 999,
          px: '11px',
          py: '4px',
          bgcolor: after ? C.p100 : C.n200,
          color: after ? C.p800 : C.n700,
        }}
      >
        {tag}
      </Box>
    </Box>
  );
}

function FeedPanel({
  variant,
  rows,
}: {
  variant: 'before' | 'after';
  rows: {title: string; tag: string}[];
}): React.JSX.Element {
  const after = variant === 'after';
  return (
    <Box sx={{bgcolor: after ? C.p100 : C.n100, borderRadius: '32px', p: '28px'}}>
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: '18px'}}>
        <Box
          component="span"
          sx={{
            fontSize: 13,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: after ? C.p800 : ink(60),
          }}
        >
          {after ? 'After' : 'Before'}
        </Box>
        <Box component="span" sx={{fontSize: 13, color: ink(60)}}>
          {after ? 'what GoodShowHQ serves' : 'what the algorithm serves'}
        </Box>
      </Box>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
        {rows.map(r => (
          <FeedRow key={r.title} title={r.title} tag={r.tag} variant={variant} />
        ))}
      </Box>
    </Box>
  );
}

function Faq({q, a}: {q: string; a: string}): React.JSX.Element {
  return (
    <Box
      component="details"
      sx={{
        bgcolor: C.n100,
        borderRadius: '24px',
        p: '20px 26px',
        '& > summary': {listStyle: 'none', cursor: 'pointer'},
        '& > summary::-webkit-details-marker': {display: 'none'},
        '&[open] .faq-chev': {transform: 'rotate(180deg)'},
      }}
    >
      <Box
        component="summary"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          fontWeight: 700,
          fontSize: 16.5,
          lineHeight: 1.4,
        }}
      >
        {q}
        <Box
          className="faq-chev"
          component="span"
          sx={{
            flex: 'none',
            display: 'grid',
            placeContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: C.accent100,
            color: C.accent700,
            transition: 'transform 0.2s',
          }}
        >
          {ChevIcon}
        </Box>
      </Box>
      <Typography sx={{fontSize: 15.5, lineHeight: 1.65, mt: '14px', maxWidth: '58ch', color: ink(78)}}>
        {a}
      </Typography>
    </Box>
  );
}

/* ---- "How the AI works" section (#ai) -------------------------------- */
const SHADOW_MD = '0 3px 10px color-mix(in srgb, oklch(0.26 0.04 280) 12%, transparent)';

const tagBase = {
  fontSize: 12.5,
  fontWeight: 600,
  borderRadius: 999,
  px: '11px',
  py: '4px',
  whiteSpace: 'nowrap',
  display: 'inline-block',
} as const;
const tagPeri = {...tagBase, bgcolor: C.p100, color: C.p800};
const tagAccent = {...tagBase, bgcolor: C.accent100, color: C.accent700};
const tagOutline = {...tagBase, bgcolor: 'transparent', color: ink(60), border: `1px solid ${ink(20)}`};

const BookIcon = (
  <svg
    width={20}
    height={20}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M2 4h6a4 4 0 0 1 4 4v12a3 3 0 0 0-3-3H2z" />
    <path d="M22 4h-6a4 4 0 0 0-4 4v12a3 3 0 0 1 3-3h7z" />
  </svg>
);
const SlidersIcon = (
  <Stroke d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" size={20} />
);

const ANALYSIS_ROWS = [
  {label: 'Educational value', value: '9.1 / 10'},
  {label: 'Age-appropriate', value: 'Great fit'},
  {label: 'Pacing', value: 'Calm'},
  {label: 'Creator credibility', value: 'Verified'},
  {label: 'Commercial content', value: 'None'},
];
const PRIORITIES = [
  {label: 'Educational depth', pct: 85},
  {label: 'Calm pacing', pct: 90},
  {label: 'Creativity & art', pct: 65},
];
const TOPICS_IN = ['Space', 'Marine biology', 'Music', 'Chess'];
const TOPICS_ADD = ['Drawing', 'Coding'];

function IconCircle({
  bg,
  fg,
  children,
}: {
  bg: string;
  fg: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        bgcolor: bg,
        color: fg,
        display: 'grid',
        placeContent: 'center',
        mb: '20px',
      }}
    >
      {children}
    </Box>
  );
}
function AiH3({children}: {children: React.ReactNode}): React.JSX.Element {
  return (
    <Typography
      component="h3"
      sx={{
        fontFamily: FONT_HEAD,
        fontWeight: 400,
        fontSize: 'clamp(22px,2.4vw,28px)',
        lineHeight: 1.25,
        m: '0 0 14px',
      }}
    >
      {children}
    </Typography>
  );
}
function AiP({children, last}: {children: React.ReactNode; last?: boolean}): React.JSX.Element {
  return (
    <Typography sx={{fontSize: 15.5, lineHeight: 1.65, m: last ? 0 : '0 0 14px', maxWidth: '52ch', color: ink(78)}}>
      {children}
    </Typography>
  );
}
function CardLabel({children, sx}: {children: React.ReactNode; sx?: object}): React.JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        fontSize: 12.5,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
        color: ink(60),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function AiSection(): React.JSX.Element {
  const grid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '48px',
    alignItems: 'center',
  } as const;
  const card = {bgcolor: C.n100, borderRadius: '32px', p: '28px', boxShadow: SHADOW_MD} as const;
  return (
    <Box component="section" id="ai" sx={{pt: '48px', pb: '72px'}}>
      <Kicker>How the AI works</Kicker>
      <H2 sx={{mb: '56px', maxWidth: '24ch'}}>An agent that watches every video so you don’t have to.</H2>

      {/* Block A — report card */}
      <Box sx={{...grid, mb: '72px'}}>
        <Box>
          <IconCircle bg={C.accent100} fg={C.accent700}>{BookIcon}</IconCircle>
          <AiH3>Every video gets a report card.</AiH3>
          <AiP>
            Before a video shows up in your kid’s feed, our agent reads the transcript, listens to
            the audio, and watches for visual cues — then scores it on the factors that actually
            matter: educational value, age fit, pacing, creator credibility, commercial pressure,
            language, and more.
          </AiP>
          <AiP last>
            Tap any video in your dashboard to see why the AI scored it the way it did — and
            override any decision in one tap.
          </AiP>
        </Box>
        <Box sx={card}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', mb: '18px'}}>
            <CardLabel>Video analysis</CardLabel>
            <Box component="span" sx={tagPeri}>Approved · age 9</Box>
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: '14px', bgcolor: C.bg, borderRadius: '24px', p: '14px 18px', mb: '18px'}}>
            <Box sx={{flex: 'none', width: 40, height: 40, borderRadius: '50%', bgcolor: C.accent, color: '#fff', display: 'grid', placeContent: 'center'}}>
              {PlayIcon}
            </Box>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontWeight: 700, fontSize: 15.5, lineHeight: 1.3}}>How volcanoes actually work</Typography>
              <Typography sx={{fontSize: 13.5, color: ink(60), mt: '2px'}}>SciShow Kids · 8 min</Typography>
            </Box>
          </Box>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
            {ANALYSIS_ROWS.map(r => (
              <Box key={r.label} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', p: '8px 4px'}}>
                <Typography sx={{fontSize: 14.5, fontWeight: 600}}>{r.label}</Typography>
                <Box component="span" sx={tagPeri}>{r.value}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Block B — priorities */}
      <Box sx={grid}>
        <Box sx={card}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', mb: '22px'}}>
            <CardLabel>Your priorities</CardLabel>
            <Box component="span" sx={tagAccent}>Maya’s profile</Box>
          </Box>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: '18px'}}>
            {PRIORITIES.map(p => (
              <Box key={p.label}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: '8px'}}>
                  <Typography sx={{fontSize: 14.5, fontWeight: 600}}>{p.label}</Typography>
                  <Typography sx={{fontSize: 14.5, fontWeight: 700, color: C.accent700}}>{p.pct}%</Typography>
                </Box>
                <Box sx={{height: 8, borderRadius: 999, bgcolor: C.n200, overflow: 'hidden'}}>
                  <Box sx={{height: '100%', borderRadius: 999, bgcolor: C.accent, width: `${p.pct}%`}} />
                </Box>
              </Box>
            ))}
          </Box>
          <CardLabel sx={{mt: '24px', mb: '12px'}}>Topics she’s into</CardLabel>
          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
            {TOPICS_IN.map(t => (
              <Box key={t} component="span" sx={tagAccent}>{t} ✓</Box>
            ))}
            {TOPICS_ADD.map(t => (
              <Box key={t} component="span" sx={tagOutline}>+ {t}</Box>
            ))}
          </Box>
        </Box>
        <Box>
          <IconCircle bg={C.p100} fg={C.p800}>{SlidersIcon}</IconCircle>
          <AiH3>You set the priorities. The feed tunes itself.</AiH3>
          <AiP>
            Parents don’t agree on everything, and neither should their kids’ feeds. Dial in what
            matters for <em>your</em> family — how much you care about educational depth, how much
            you care about calm pacing, what subjects they’re into right now.
          </AiP>
          <AiP last>Change your mind any time. The feed re-tunes within a day.</AiP>
        </Box>
      </Box>
    </Box>
  );
}

/* ---- page ------------------------------------------------------------ */
function LandingPageBody(): React.JSX.Element {
  const [joined, setJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  async function joinWaitlist(): Promise<void> {
    const email = emailRef.current?.value.trim() ?? '';
    if (!email || !email.includes('@')) {
      setErr('Enter a valid email.');
      emailRef.current?.focus();
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify({email, source: 'landing'}),
      });
      if (!res.ok) throw new Error(String(res.status));
      setJoined(true);
    } catch {
      setErr('Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const content = {maxWidth: MAXW, mx: 'auto', px: PADX} as const;

  return (
    <Box
      sx={{
        bgcolor: C.bg,
        color: C.ink,
        fontFamily: FONT_BODY,
        overflowX: 'clip',
        textWrap: 'pretty',
        minHeight: '100vh',
      }}
    >
      {/* 1. Nav */}
      <Box
        component="nav"
        sx={{
          ...content,
          display: 'flex',
          alignItems: 'center',
          gap: {xs: '16px', md: '28px'},
          py: '20px',
        }}
      >
        <Box
          component="a"
          href="#/"
          aria-label="GoodShowHQ home"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            mr: 'auto',
            textDecoration: 'none',
          }}
        >
          <Box component="img" src={logoMark} alt="" sx={{height: 30, width: 'auto', display: 'block'}} />
          <Box
            component="span"
            sx={{fontFamily: FONT_HEAD, fontWeight: 400, fontSize: 21, color: C.ink}}
          >
            GoodShowHQ
          </Box>
        </Box>
        {[
          {label: 'How it works', on: () => scrollToId('how')},
          {label: 'FAQ', on: () => scrollToId('faq')},
          {label: 'Get the app', on: () => scrollToId('download')},
        ].map(l => (
          <Box
            key={l.label}
            component="button"
            onClick={l.on}
            sx={{
              display: {xs: 'none', md: 'inline'},
              background: 'none',
              border: 0,
              p: 0,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontFamily: FONT_BODY,
              fontSize: 15,
              fontWeight: 500,
              color: C.accent700,
              '&:hover': {color: C.accent700, textDecoration: 'underline'},
            }}
          >
            {l.label}
          </Box>
        ))}
        <Box
          component="a"
          href="#/downloads"
          sx={{
            display: {xs: 'none', md: 'inline'},
            whiteSpace: 'nowrap',
            fontSize: 15,
            fontWeight: 500,
            color: C.accent700,
            textDecoration: 'none',
            '&:hover': {textDecoration: 'underline'},
          }}
        >
          Downloads
        </Box>
        <Box component="button" onClick={() => scrollToId('join')} sx={navPrimary}>
          Get started
        </Box>
      </Box>

      <Box sx={content}>
        {/* 2. Hero */}
        <Box component="section" sx={{position: 'relative', pt: 'clamp(64px,9vw,120px)', pb: '84px'}}>
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              right: 'min(0px, calc(1160px - 100vw))',
              top: -220,
              width: 420,
              height: 420,
              borderRadius: '50%',
              bgcolor: C.p200,
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
          {/* hero content sits above the decorative circle */}
          <Box sx={{position: 'relative', zIndex: 1}}>
          <Typography
            component="h1"
            sx={{
              fontFamily: FONT_HEAD,
              fontWeight: 400,
              fontSize: 'clamp(40px,5.6vw,72px)',
              lineHeight: 1.08,
              m: '0 0 0 -0.028em',
              maxWidth: '16ch',
            }}
          >
            <Box component="span" sx={{display: 'block'}}>
              YouTube is where your kid is.
            </Box>
            <Box component="span" sx={{display: 'block', color: C.accent}}>
              Make it worth their hour.
            </Box>
          </Typography>
          <Typography sx={{fontSize: 18, lineHeight: 1.65, maxWidth: '52ch', mt: '36px'}}>
            You’re not going to win the fight against YouTube — and you don’t have to.
            GoodShowHQ gently guides your kid toward better choices, nudging their feed toward the
            good stuff at a pace they never notice. Less junk. More curiosity. No new app to argue
            about.
          </Typography>
          <Box sx={{display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', mt: '36px'}}>
            <Box component="button" onClick={() => scrollToId('download')} sx={btnPrimary}>
              Get started
            </Box>
            <Box component="button" onClick={() => scrollToId('how')} sx={btnSecondary}>
              See how it works
            </Box>
          </Box>
          <Box sx={{display: 'flex', gap: '10px', flexWrap: 'wrap', mt: '28px'}}>
            {['Works inside regular YouTube', 'Nothing installs on your kid’s device', 'COPPA-compliant'].map(
              t => (
                <Box
                  key={t}
                  component="span"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: 999,
                    px: '14px',
                    py: '7px',
                    bgcolor: C.p100,
                    color: C.p800,
                  }}
                >
                  {t}
                </Box>
              ),
            )}
          </Box>
          </Box>
        </Box>

        {/* 3. The difference */}
        <Box component="section" id="difference" sx={{pt: '36px', pb: '72px'}}>
          <Kicker>The difference</Kicker>
          <H2 sx={{mb: '12px'}}>Same kid. Same YouTube. A better hour.</H2>
          <Typography sx={{fontSize: 16, lineHeight: 1.65, maxWidth: '52ch', mb: '40px', color: ink(78)}}>
            We don’t block or lock anything. We make the better choice the easy one — slowly,
            so it sticks — by telling the algorithm what your family actually values.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '28px',
              alignItems: 'start',
            }}
          >
            <FeedPanel variant="before" rows={BEFORE_ROWS} />
            <FeedPanel variant="after" rows={AFTER_ROWS} />
          </Box>
        </Box>

        {/* 4. How it works */}
        <Box component="section" id="how" sx={{pt: '48px', pb: '72px'}}>
          <Kicker>How it works</Kicker>
          <H2 sx={{mb: '44px', maxWidth: '22ch'}}>Three steps, and the third one is waiting.</H2>
          <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '36px'}}>
            {STEPS.map(s => (
              <Box key={s.n}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: s.bg,
                    color: s.fg,
                    display: 'grid',
                    placeContent: 'center',
                    fontFamily: FONT_HEAD,
                    fontWeight: 400,
                    fontSize: 26,
                    mb: '20px',
                  }}
                >
                  {s.n}
                </Box>
                <Typography component="h3" sx={{fontFamily: FONT_HEAD, fontWeight: 400, fontSize: 22, m: '0 0 10px'}}>
                  {s.title}
                </Typography>
                <Typography sx={{fontSize: 15.5, lineHeight: 1.65, m: 0, maxWidth: '34ch', color: ink(78)}}>
                  {s.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* How the AI works (#ai) — report card + priorities */}
        <AiSection />

        {/* 5. Testimonials */}
        <Box component="section" sx={{pt: '48px', pb: '72px'}}>
          <Kicker sx={{mb: '28px'}}>From our pilot families</Kicker>
          <Box component="figure" sx={{m: 0}}>
            <Box
              component="blockquote"
              sx={{
                fontFamily: FONT_HEAD,
                fontWeight: 400,
                fontSize: 'clamp(24px,2.6vw,32px)',
                lineHeight: 1.4,
                maxWidth: '32ch',
                m: 0,
                textIndent: '-0.559em',
              }}
            >
              “He still watches an hour a day. But now he comes to dinner talking about
              volcanoes instead of somebody’s feud. I didn’t take anything away —
              that’s the part I can’t get over.”
            </Box>
            <Box
              component="figcaption"
              sx={{fontSize: 15.5, lineHeight: 1.65, mt: '28px', color: ink(70), textIndent: '-1.347em'}}
            >
              — Dana M., pilot parent of a 9-year-old
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '24px',
              mt: '48px',
            }}
          >
            {TESTIMONIALS.map(t => (
              <Box key={t.by} sx={{bgcolor: C.n100, borderRadius: '24px', p: '24px'}}>
                <Typography sx={{m: 0, fontSize: 15.5, lineHeight: 1.6}}>{t.q}</Typography>
                <Typography sx={{mt: '12px', fontSize: 13.5, fontWeight: 600, color: ink(60)}}>{t.by}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* 6. Get the app */}
        <Box component="section" id="download" sx={{pt: '48px', pb: '72px'}}>
          <Box
            sx={{
              bgcolor: C.n100,
              borderRadius: '32px',
              p: 'clamp(36px,5vw,64px)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '40px',
              alignItems: 'center',
            }}
          >
            <Box>
              <Kicker>Get the app</Kicker>
              <H2 sx={{fontSize: 'clamp(26px,3vw,36px)', mb: '12px'}}>
                On your phone. On your desk. Never on theirs.
              </H2>
              <Typography sx={{fontSize: 16, lineHeight: 1.65, maxWidth: '46ch', m: 0, color: ink(78)}}>
                The GoodShowHQ apps are for parents — review flagged videos, tune the feed, and
                see what’s landing. Your kid’s device stays untouched.
              </Typography>
            </Box>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px', justifySelf: 'start', minWidth: 260}}>
              <Box component="a" href="#/get" sx={btnPrimary}>
                {AppleIcon}&nbsp; Download for iPhone
              </Box>
              <Box component="a" href={MAC_URL} sx={btnSecondary}>
                {LaptopIcon}&nbsp; Download for Mac
              </Box>
              <Box component="a" href={WIN_URL} sx={btnSecondary}>
                {LaptopIcon}&nbsp; Download for Windows
              </Box>
            </Box>
          </Box>
        </Box>

        {/* 7. FAQ */}
        <Box component="section" id="faq" sx={{pt: '48px', pb: '72px'}}>
          <Kicker>Questions parents actually ask</Kicker>
          <H2 sx={{mb: '36px'}}>The short answers.</H2>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: 760}}>
            {FAQS.map(f => (
              <Faq key={f.q} q={f.q} a={f.a} />
            ))}
          </Box>
        </Box>

        {/* 8. Waitlist close */}
        <Box component="section" id="join" sx={{pb: '56px'}}>
          <Box sx={{bgcolor: C.p100, borderRadius: '32px', p: 'clamp(36px,5vw,64px)'}}>
            <Typography
              component="h3"
              sx={{
                fontFamily: FONT_HEAD,
                fontWeight: 400,
                fontSize: 'clamp(26px,3vw,34px)',
                lineHeight: 1.2,
                m: 0,
                maxWidth: '24ch',
              }}
            >
              Want in early? Join the waitlist.
            </Typography>
            <Typography sx={{fontSize: 16, lineHeight: 1.65, mt: '18px', maxWidth: '52ch', color: ink(78)}}>
              We’re letting new features out to families a few hundred at a time so every feed
              gets tuned properly. Leave your email and we’ll hold your place — no spam,
              just one note when it’s your turn.
            </Typography>
            <Box
              component="form"
              onSubmit={(e: React.FormEvent) => {
                e.preventDefault();
                void joinWaitlist();
              }}
              sx={{
                display: 'flex',
                gap: '12px',
                alignItems: 'stretch',
                flexWrap: 'wrap',
                mt: '28px',
                maxWidth: 520,
                '& .wl-input': {
                  flex: 1,
                  minWidth: 220,
                  minHeight: 44,
                  borderRadius: 999,
                  border: `1.5px solid ${ink(18)}`,
                  px: '20px',
                  fontFamily: FONT_BODY,
                  fontSize: 15.5,
                  color: C.ink,
                  bgcolor: C.bg,
                  boxSizing: 'border-box',
                },
                '& .wl-input::placeholder': {color: ink(45)},
                '& .wl-input:focus-visible': {
                  outline: `2px solid ${C.accent}`,
                  outlineOffset: 2,
                  borderColor: 'transparent',
                },
              }}
            >
              <input
                ref={emailRef}
                className="wl-input"
                type="email"
                placeholder="you@example.com"
                aria-label="Email address"
                disabled={joined}
              />
              <Box
                component="button"
                type="submit"
                disabled={submitting || joined}
                sx={{...btnPrimary, opacity: submitting || joined ? 0.7 : 1}}
              >
                {submitting ? 'Joining…' : 'Join the waitlist'}
              </Box>
            </Box>
            {joined ? (
              <Typography sx={{fontSize: 15.5, mt: '16px', color: C.p800, fontWeight: 600}}>
                You’re on the list. Talk soon.
              </Typography>
            ) : err ? (
              <Typography sx={{fontSize: 14.5, mt: '12px', color: C.accent700, fontWeight: 600}}>{err}</Typography>
            ) : null}
          </Box>
        </Box>

        {/* 9. Footer */}
        <Box component="footer" sx={{pt: '32px', pb: '56px', fontSize: 13, lineHeight: 1.6, color: ink(70)}}>
          GoodShowHQ · Not affiliated with YouTube or Google LLC · © 2026 GoodShowHQ, Inc.
        </Box>
      </Box>
    </Box>
  );
}

export default function LandingPage(): React.JSX.Element {
  return (
    <ThemeProvider theme={goodShowHQTheme}>
      <CssBaseline />
      <LandingPageBody />
    </ThemeProvider>
  );
}
